
  
function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

//   onButtonClick(event) {
//   event.preventDefault();
//   const slideWidth = this.sliderItemsToShow[0].clientWidth;
//   const direction = event.currentTarget.name === 'next' ? 1 : -1;
//   this.slideScrollPosition = this.slider.scrollLeft + direction * slideWidth;
//   this.setSlidePosition(this.slideScrollPosition);
// }


  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);
 



class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}
  document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('slider-component').forEach((sliderComp) => {
    const slider = sliderComp.querySelector('.slider');
    const slides = slider?.querySelectorAll('.slider__slide') || [];
    const progressBar = sliderComp.querySelector('.slider-progress-bar');
    const prevBtn = sliderComp.querySelector('.slider-button--prev');
    const nextBtn = sliderComp.querySelector('.slider-button--next');

    if (!slider || !progressBar || slides.length === 0) return;

    let currentIndex = 0;

    const getSlidesPerView = () => {
      const width = window.innerWidth;
      if (width <= 749) return 2;
      if (width <= 989) return 2;
      if (width <= 1300) return 3;
      return 4;
    };

    const updateProgress = () => {
      const visibleSlides = getSlidesPerView();
      const maxIndex = slides.length - visibleSlides;
      const percentage = (currentIndex / maxIndex) * 100;
      progressBar.style.width = `${Math.min(100, percentage)}%`;
    };

    const goToSlide = (index) => {
      const slideWidth = slides[0].offsetWidth;
      slider.scrollTo({
        left: index * slideWidth,
        behavior: 'smooth',
      });
      currentIndex = index;
      updateProgress();
    };

    prevBtn?.addEventListener('click', () => {
      const visibleSlides = getSlidesPerView();
      currentIndex = Math.max(0, currentIndex - visibleSlides);
      goToSlide(currentIndex);
    });

    nextBtn?.addEventListener('click', () => {
      const visibleSlides = getSlidesPerView();
      const slideWidth = slides[0].offsetWidth;
      const maxScrollLeft = slider.scrollWidth - slider.clientWidth;

      let nextIndex = currentIndex + visibleSlides;
      let nextScrollLeft = nextIndex * slideWidth;

      if (nextScrollLeft > maxScrollLeft) {
        nextScrollLeft = maxScrollLeft;
        currentIndex = slides.length - visibleSlides;
      } else {
        currentIndex = nextIndex;
      }

      slider.scrollTo({
        left: nextScrollLeft,
        behavior: 'smooth',
      });

      updateProgress();
    });

    window.addEventListener('resize', () => {
      updateProgress();
    });

    slider.addEventListener('scroll', () => {
      const slideWidth = slides[0].offsetWidth;
      const visibleSlides = getSlidesPerView();
      const scrollLeft = slider.scrollLeft;

      const approxIndex = Math.round(scrollLeft / slideWidth);
      const maxIndex = slides.length - visibleSlides;
      currentIndex = Math.min(approxIndex, maxIndex);
      updateProgress();
    });

    setTimeout(() => {
      goToSlide(0);
    }, 100);
  });
});



 
// Initialize sliders on page load
$(document).ready(function () {


  $('.slider-reviews-columns').slick({
  dots: true,
  infinite: true,
  slidesToShow: 4,
  slidesToScroll: 1,
  arrows: true,
  touchThreshold: 20,  
  touchMove: true,
  prevArrow: $('.taste-reviews-section .slick-cprev'),
  nextArrow: $('.taste-reviews-section .slick-cnext'),  
  accessibility: false, 
  responsive: [
    {
      breakpoint: 1301,
      settings: {
        slidesToShow: 3,
        slidesToScroll: 1,
        infinite: true,
        dots: true
      }
    },
    {
      breakpoint: 990,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1
      }
    },
    {
      breakpoint: 750,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1
      }
    }
   
  ]
  });
//  $('.store-slider-logo').slick({
//   dots: false,
//   infinite: true,
//   speed: 8000, // longer = slower scroll
//   slidesToShow: 6,
//   slidesToScroll: 1,
//   autoplay: true,
//   autoplaySpeed: 0, // immediate restart
//   arrows: false,
//   cssEase: 'linear', // smooth motion
//   swipe: false,
//   draggable: false,
//   focusOnSelect: false,
//   pauseOnFocus: false,
//   pauseOnHover: false,
//   variableWidth: true, // needed for continuous feel
//   accessibility: true,

//   responsive: [
//     {
//       breakpoint: 1024,
//       settings: { slidesToShow: 4 }
//     },
//     {
//       breakpoint: 750,
//       settings: { slidesToShow: 3 }
//     },
//     {
//       breakpoint: 480,
//       settings: { slidesToShow: 2 }
//     }
//   ]
// });

// var $marquee = $('.store-slider-logo');

// $marquee.on('click', '.slick-slide', function (e) {
//   e.preventDefault();
  
//   var index = $(this).data('slick-index');
  
//   // Jump instantly to clicked slide without animation
//   $marquee.slick('slickGoTo', index, true); // true = no animation
  
//   // Immediately restart the linear autoplay
//   $marquee.slick('slickPlay');
// });


// function initLucilleGalleryPagination() {
//   var $gallery = $('.lucille-gallery-static > ul');

//   // Check if Slick is initialized
//   if ($gallery.hasClass('slick-initialized')) {
//     $gallery.on('init reInit afterChange', function(event, slick, currentSlide) {
//       var i = (currentSlide ? currentSlide : 0) + 1;
//       $('.slidePagi').text(i + '/' + slick.slideCount);
//     });

//     // Trigger manually in case it's already initialized
//     var slick = $gallery.slick('getSlick');
//     $('.slidePagi').text((slick.currentSlide + 1) + '/' + slick.slideCount);
//   } else {
//     // Retry until initialized
//     setTimeout(initLucilleGalleryPagination, 300);
//   }
// }

// // Start the check
// initLucilleGalleryPagination();

//  $('.lucille-gallery-static > ul').slick({
//   dots: false,
//   infinite: true,
//   slidesToShow: 1,
//   slidesToScroll: 1,
//   arrows: true,
//   touchThreshold: 20,  
//   touchMove: true,
//   prevArrow: $('.lucille-gallery-static .slick-prev-pdp'),
//   nextArrow: $('.lucille-gallery-static .slick-next-pdp'),
//   accessibility: false,
  
//   });


// var $mainSlider = $('.lucille-gallery-static > ul');
// var $thumbSlider = $('.lucille-thumbnail-list');

// // MAIN SLIDER
// $mainSlider.on('init reInit afterChange', function (event, slick, currentSlide) {
//   var i = (currentSlide === undefined ? slick.currentSlide : currentSlide) + 1;
//   $('.slidePagi').text(i + '/' + slick.slideCount);
// });

// $mainSlider.slick({
//   slidesToShow: 1,
//   slidesToScroll: 1,
//   arrows: true,
//   infinite: true,
//   asNavFor: '.lucille-thumbnail-list',
//   prevArrow: $('.lucille-gallery-static .slick-prev-pdp'),
//   nextArrow: $('.lucille-gallery-static .slick-next-pdp'),
//   accessibility: false,
//   touchMove: true
// });

// // THUMBNAILS
// $thumbSlider.slick({
//   slidesToShow: 5,
//   slidesToScroll: 1,
//   asNavFor: '.lucille-gallery-static > ul',
//   arrows: false,
//   dots: false,
//   focusOnSelect: true,
//   infinite: true
// });


var $mainSlider = $('.lucille-gallery-static > ul');

$mainSlider.slick({
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: true,      
  dots: true,        
  infinite: true,
  prevArrow: $('.lucille-gallery-static .slick-prev-pdp'),
  nextArrow: $('.lucille-gallery-static .slick-next-pdp'),
  accessibility: false,
  touchMove: true
});




 $('.product-merch-slider').slick({
  dots: true,
  infinite: false,
  slidesToShow: 3,
  slidesToScroll: 1,
  arrows: true,
  touchThreshold: 20,  
  touchMove: true,
  prevArrow: $('.merch-collection-block .slick-cprev'),
  nextArrow: $('.merch-collection-block .slick-cnext'),
  accessibility: false,
  responsive: [
     
    {
      breakpoint: 991,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1
      }
    },
    {
      breakpoint: 750,
       settings: "unslick"
    }
   
  ]
  });



var $slider = $('.instagram-slider-row');

$slider.on('init reInit afterChange', function (event, slick, currentSlide) {

  var slideCount = slick.slideCount;
  var currentIndex = currentSlide !== undefined ? currentSlide : 0;

  var progressPercent = ((currentIndex + 1) / slideCount) * 100;

  $('.insta-progress-track').css({
    width: progressPercent + '%'
  });

});

 $('.instagram-slider-row').slick({
  dots: false,
  infinite: true,
  swipe: true,
  slidesToShow: 4,
  slidesToScroll: 1,
  arrows: true,
  swipeToSlide: true,
  draggable: true,
  touchThreshold: 20,  
  touchMove: true,
  prevArrow: $('.instagram-review-outer .slick-cprev'),
  nextArrow: $('.instagram-review-outer .slick-cnext'),
  accessibility: false,
   customPaging: function (slider, i) {
        var slideNumber2 = i + 1;
        // Each dot wrapped in <li> with role, and button with aria-label
        return '<button type="button" role="tab" aria-label="Go to slide ' + slideNumber2 + '">' +
                       slideNumber2 +
                   '</button>';
    },
  responsive: [
    {
      breakpoint: 1301,
      settings: {
        slidesToShow: 3,
        slidesToScroll: 1
      }
    },
    {
      breakpoint: 990,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1
      }
    },
    {
      breakpoint: 750,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1
      
      }
    },
    {
      breakpoint: 361,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1
      }
    }  
     
  ]
  });







  
//  $('.collections-pdp-slider').slick({
//   dots: false,
//   infinite: true,
//   swipe: true,
  
//   slidesToShow: 4,
//   slidesToScroll: 1,
//   arrows: true,
//   swipeToSlide: true,
//   draggable: true,
//   touchThreshold: 20,  
//   touchMove: true,
//   prevArrow: $('.collections-pdp-outer .slick-cprev'),
//   nextArrow: $('.collections-pdp-outer .slick-cnext'),
//   accessibility: false,
//    customPaging: function (slider, i) {
//         var slideNumber2 = i + 1;
//         // Each dot wrapped in <li> with role, and button with aria-label
//         return '<button type="button" role="tab" aria-label="Go to slide ' + slideNumber2 + '">' +
//                        slideNumber2 +
//                    '</button>';
//     },
//   responsive: [
//     {
//       breakpoint: 1251,
//       settings: {
//         slidesToShow: 4,
//         slidesToScroll: 1
//       }
//     },
//     {
//       breakpoint: 990,
//       settings: {
//         slidesToShow: 4,
//         slidesToScroll: 1
//       }
//     },
//     {
//       breakpoint: 750,
//       settings: {
//         slidesToShow: 3,
//         slidesToScroll: 1
      
//       }
//     },
//     {
//       breakpoint: 380,
//       settings: {
//         slidesToShow: 2,
//         slidesToScroll: 1
      
//       }
//     }  
     
//   ]
//   });

 // Initialize Slick
// $('.instagram-slider-row').slick({
//   dots: true,
//   arrows: true,
//   accessibility: true,
//   prevArrow: $('.slick-cprev'),
//   nextArrow: $('.slick-cnext'),
//   adaptiveHeight: true,
//     slidesToShow: 4,
//   slidesToScroll: 1,
// });

// // Function to load iframe only for active slides
// function loadActiveIframe(slider){
//   slider.find('.slick-active iframe').each(function(){
//     var $this = $(this);
//     if (!$this.attr('src')) {
//       $this.attr('src', $this.data('src'));
//     }
//   });
// }

// // Load iframe on init & after slide change
// $('.instagram-slider-row').on('init afterChange', function(event, slick){
//   loadActiveIframe($(slick.$slider));
// });

// // Trigger initial load after Slick init
// $('.instagram-slider-row').slick('setPosition');



  $('.inside-outer').slick({
  dots: false,
  infinite: true,
  slidesToShow: 6,
  slidesToScroll: 1,
  arrows: true,
  touchThreshold: 20,         // very sensitive to short swipes
  touchMove: true,
  prevArrow: $('.ins-arrow .ins-slick-prev'),
  nextArrow: $('.ins-arrow .ins-slick-next'),
  accessibility: false,
   customPaging: function (slider, i) {
        var slideNumber = i + 1;
        // Each dot wrapped in <li> with role, and button with aria-label
        return '<button type="button" role="tab" aria-label="Go to slide ' + slideNumber + '">' +
                       slideNumber +
                   '</button>';
    },
  responsive: [
    {
      breakpoint: 1251,
      settings: {
        slidesToShow: 5,
        slidesToScroll: 1
      }
    },
    {
      breakpoint: 990,
      settings: {
        slidesToShow: 3,
        slidesToScroll: 1
      }
    },
     
    {
      breakpoint: 600,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1,
        
              
      }
    },
     {
      breakpoint: 400,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1 
              
      }
    },
     
     
  ]
  });
  
  const slides = document.querySelectorAll('.ins-slide');
  const slider = document.querySelector('.inside-outer'); 

  if (slides.length > 0) {
  slides.forEach(slide => {
    slide.addEventListener('click', e => {
      e.stopPropagation(); 
      slides.forEach(s => s.classList.remove('ins-active'));
      slide.classList.add('ins-active');
    });
  });

  document.addEventListener('click', e => {
    if (!slider.contains(e.target)) {
      slides.forEach(s => s.classList.remove('ins-active'));
    }
  });
  }

 
  $('.testimonial-grid').slick({
  dots: false,
  infinite: true,
  slidesToShow: 3,
  slidesToScroll: 1,
  arrows: false,
  touchThreshold: 20,         // very sensitive to short swipes
  touchMove: true,
  // prevArrow: $('.ins-arrow .ins-slick-prev'),
  // nextArrow: $('.ins-arrow .ins-slick-next'),
  accessibility: false,
   customPaging: function (slider, i) {
        var slideNumber = i + 1;
        // Each dot wrapped in <li> with role, and button with aria-label
        return '<button type="button" role="tab" aria-label="Go to slide ' + slideNumber + '">' +
                       slideNumber +
                   '</button>';
    },
  responsive: [
     
    {
      breakpoint: 990,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1
      }
    },
     
    {
      breakpoint: 750,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1 
              
      }
    } 
     
     
  ]
  });


  $('.product-ss-grid').slick({
  dots: false,
  infinite: true,
  slidesToShow: 3,
  slidesToScroll: 1,
  arrows: true,
  touchThreshold: 20,         // very sensitive to short swipes
  touchMove: true,
  prevArrow: $('.product-ss-main .slick-cprev'),
  nextArrow: $('.product-ss-main .slick-cnext'),
  accessibility: false,
   customPaging: function (slider, i) {
        var slideNumber = i + 1;
        // Each dot wrapped in <li> with role, and button with aria-label
        return '<button type="button" role="tab" aria-label="Go to slide ' + slideNumber + '">' +
                       slideNumber +
                   '</button>';
    },
  responsive: [
    {
      breakpoint: 990,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1
      }
    },
     
    {
      breakpoint: 750,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1 
              
      }
    } 
     
  ]
  });
  
  $(document).on('click', '.question-buttons a[href^="#"], .question-buttons a[href*="#"]', function(e) {
    const target = $($(this).attr('href'));
    if (target.length) {
      e.preventDefault();
      $('html, body').animate({
        scrollTop: target.offset().top-125
      }, 500); 
    }
  });

  // $(".collection-main-all li.grid__item").slice(0, 8).show();
  // $("body").on('click touchstart', '.load-more-custom', function (e) {
  // 	e.preventDefault();
  // 	$(".collection-main-all li.grid__item:hidden").slice(0, 8).show();
  // 	if ($(".collection-main-all li.grid__item:hidden").length == 0) {
  // 		$(".load-more-custom").css('visibility', 'hidden');
  // 	}
  // 	$('html,body').animate({
  // 		scrollTop: $(this).offset().top
  // 	}, 1000);
  // });
  
 // initThumbnailSliders();
  
  $('.quick-add .quick-add__submit').hover(
  function () {
  
    $(this).closest('li.grid__item, li.grid__item__slide, li.slider-grid__item').addClass('hover-active');
  },
  function () {
    
    $(this).closest('li.grid__item, li.grid__item__slide, li.slider-grid__item').removeClass('hover-active');
  }
);

  
 
  var itemsToShow = 2;

  function mobileLoadMore() {
    if ($(window).width() <= 749) {
      var hiddenItems = $(".product-merch-slider .slider-grid__item:hidden");
      if (hiddenItems.length <= 2) {
        $(".merch-load-more").hide();
      } else {
        $(".merch-load-more").show();
      }
    } else {
      // Desktop mode: hide button
      $(".merch-load-more").hide();
    }
  }

  $(".merch-load-more a").on("click", function () {
    var hiddenItems = $(".product-merch-slider .slider-grid__item:hidden");
    hiddenItems.slice(0, itemsToShow).fadeIn();
    if (hiddenItems.length <= itemsToShow) {
      $(".merch-load-more").fadeOut();
    }
  });

  // Init check
  mobileLoadMore();
  $(window).on("resize", mobileLoadMore);
  
});
 

// Collection Load More Button Custom GBC
  var $grid = $('#product-grid');
  var $btn = $('.load-more-btn');
  var $spinner = $('.load-more-spinner');

  $btn.on('click', function(e) {
    e.preventDefault();
    var nextUrl = $grid.data('next-url');
    if (!nextUrl) return;

    $btn.hide();
    $spinner.show();

    $.get(nextUrl, function(pageHTML) {
      var $nextGrid = $(pageHTML).find('#product-grid');
      var newItems = $nextGrid.html();
      var newUrl = $nextGrid.data('next-url');

      $grid.append(newItems);
      $grid.data('next-url', newUrl);

      if (newUrl) {
        $btn.show();
      } else {
        $btn.hide();
      }
      $spinner.hide();
    });
  });


// Reinitialize on window resize (debounced for performance)
// var resizeTimer;
// $(window).on('resize', function () {
//   clearTimeout(resizeTimer);
//   resizeTimer = setTimeout(function () {
//       initThumbnailSliders();
//   }, 300);
// });

// if($(".bcontact-file").length > 0) { 
// $(window).on("load", function(){
//   setTimeout(function(){
//     $(".bcontact-file").before('<span id="file-attach">Attach Files</span>');
//   }, 1000);
// });
  

// /* Hide the input[type=file] */
// document.addEventListener('DOMContentLoaded', function () {
 
//  const fileInput = document.getElementsByClassName('bcontact-file');
//  const labelText = document.getElementById('file-attach');
 
//   const noteText = document.querySelectorAll('.bcontact-field--file .bcontact-field-help-text > span');

   
//   fileInput.addEventListener('change', function () {
//     const count = this.files.length;

//     if (count > 0) {
//       labelText.textContent = count === 1
//         ? this.files[0].name
//         : `${count} file(s) selected`;
//     } else {
//       labelText.textContent = '';
//     }
//   });
// });
// }

// You can trigger logic or show/hide fields based on the subject
 window.addEventListener('load', function() {
  // Delay execution to allow dynamic content to settle
  setTimeout(function() {
    // Select all elements with the class
    const selects = document.getElementsByClassName('bcontact_select');
    if (!selects.length) return; // nothing to do if no selects found

    Array.from(selects).forEach(function(select) {
      // Handle change events
      select.addEventListener('change', function() {
        console.log('Selected subject:', this.value);
        updateSelectColor(this);
      });

      // Initialize styling on load
      updateSelectColor(select);
    });

    function updateSelectColor(el) {
      if (el.value === "") {
        el.classList.remove('selected');
      } else {
        el.classList.add('selected');
      }
    }
  }, 500); // adjust delay as needed
});


//  document.addEventListener("DOMContentLoaded", function () {
//     function initRechargeWidget() {
//       if (typeof Recharge !== 'undefined' && Recharge.Widget && window.ReChargeConfig?.product?.id) {
//         Recharge.Widget.createWidget({
//           productId: window.ReChargeConfig.product.id,
//           selector: '#RechargeWidget'
//         });
//       } else {
//         setTimeout(initRechargeWidget, 200);
//       }
//     }
//     initRechargeWidget();
//   });
 // -----------------------------
// Yotpo Refresh Helper
// -----------------------------
function refreshYotpo(retryCount = 0) {
  if (window.yotpo && typeof window.yotpo.refreshWidgets === 'function') {
    console.log("Refreshing Yotpo Widgets");
    setTimeout(() => {
      window.yotpo.refreshWidgets();
    }, 300); // give DOM a moment to settle
  } else {
    if (retryCount < 10) {
      console.log(`Yotpo not ready yet, retrying... (${retryCount})`);
      setTimeout(() => refreshYotpo(retryCount + 1), 500);
    } else {
      console.warn("Yotpo never became ready");
    }
  }
}

// -----------------------------
// Slider Init
// -----------------------------
function initYouMayLikeSlider() {
  const $slider = $('.you-may-like-slider');

  if ($slider.length && !$slider.hasClass('slick-initialized')) {
    $slider.slick({
      dots: false,
      infinite: false,
      slidesToShow: 4,
      slidesToScroll: 1,
      arrows: true,
      touchThreshold: 20,
      touchMove: true,
      prevArrow: $('.yml-slider .slick-cprev'),
      nextArrow: $('.yml-slider .slick-cnext'),
      accessibility: false,
      lazyLoad: 'ondemand',
      responsive: [
        { breakpoint: 1301, settings: { slidesToShow: 3 } },
        { breakpoint: 990,  settings: { slidesToShow: 2 } },
        { breakpoint: 750,  settings: { slidesToShow: 2 } }
      ]
    });
  }
}

// -----------------------------
// Handle Recommendations
// -----------------------------
function handleRecommendationsSection(section) {
  const $sliderContainer = $(section).find('.you-may-like-slider');
  if ($sliderContainer.length === 0) return;

  const tryInit = () => {
    if ($sliderContainer.find('.grid__item').length > 0) {
      initYouMayLikeSlider();

      // Only refresh Yotpo when its containers exist
      if ($sliderContainer.find('.yotpo').length > 0) {
        refreshYotpo();
      } else {
        const yotpoObserver = new MutationObserver(() => {
          if ($sliderContainer.find('.yotpo').length > 0) {
            refreshYotpo();
            yotpoObserver.disconnect();
          }
        });
        yotpoObserver.observe($sliderContainer[0], { childList: true, subtree: true });
      }
      return true;
    }
    return false;
  };

  // 1) Try immediately
  if (tryInit()) return;

  // 2) MutationObserver (wait until items injected)
  const observer = new MutationObserver(() => {
    if (tryInit()) {
      observer.disconnect();
    }
  });
  observer.observe($sliderContainer[0], { childList: true, subtree: true });
}

// -----------------------------
// Watch for Recommendations
// -----------------------------
function watchRecommendations() {
  const section = document.querySelector('.product-recommendations--loaded');
  if (!section) return;

  const io = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        console.log("Recommendations section in viewport");
        handleRecommendationsSection(section);
      }
    });
  }, { threshold: 0.1 });

  io.observe(section);
}

// -----------------------------
// Event Bindings
// -----------------------------
$(document).on('recommendations:loaded', '.product-recommendations--loaded', () => {
  console.log("recommendations:loaded");
  watchRecommendations();
});

$(window).on('load scroll', watchRecommendations);

$(document).on('shopify:section:load', function (event) {
  if ($(event.target).find('.you-may-like-slider').length) {
    console.log("Shopify section reloaded");
    watchRecommendations();
  }
});

$(window).on('load', function(){  
   setTimeout(function(){
      
 // Use event delegation so it works even for dynamically injected buttons
  $(document).on('mouseenter', '.quick-add .quick-add__submit', function () {
    $(this)
      .closest('li.grid__item, li.slider__slide, li.slider-grid__item')
      .addClass('hover-active');
  });

  $(document).on('mouseleave', '.quick-add .quick-add__submit', function () {
    $(this)
      .closest('li.grid__item, li.slider__slide, li.slider-grid__item')
      .removeClass('hover-active');
  });
    }, 1150);
});
 

 

//  // Function to strip aria-selected from all Slick dots
//   function stripAriaSelected() {
//     $('.slick-dots button[aria-selected]').removeAttr('aria-selected');
//     $('.slick-dots li[aria-selected]').removeAttr('aria-selected');
//     $(".slick-track").attr('aria-label', 'slider slick track');
//     $('.slick-dots').attr('role', 'list');
//     $('.slick-dots li').removeAttr('role');
//      $(".slidecarthq-overlay").attr('aria-label', 'slidecarthq button');
//   }

//   // Run after Slick initializes
//   $(document).on('init', '.slick-slider', function(event, slick){
//     stripAriaSelected();
//   });

//   // Run after every slide change
//   $(document).on('afterChange', '.slick-slider', function(event, slick){
//     stripAriaSelected();
//   });

//   // Safety: run again in case sliders init late via AJAX
//   $(document).ready(function(){
//     setTimeout(stripAriaSelected, 500);
//   });

//   (function($){
//     function fixSlickDotFocus(slider) {
//       var $dots = $(slider.$dots).find('button');
//       $dots.attr('tabindex', '-1'); // make all dots unfocusable
//       $(slider.$dots).find('.slick-active button').attr('tabindex', '0'); // active dot focusable
//     }

//     $(document).on('init', '.slick-slider', function(event, slick){
//       fixSlickDotFocus(slick);
//     });

//     $(document).on('afterChange', '.slick-slider', function(event, slick){
//       fixSlickDotFocus(slick);
//     });
 
//   })(jQuery);

(function($){

  function stripAriaSelected() {
    $('.slick-dots button[aria-selected]').removeAttr('aria-selected');
    $('.slick-dots').attr('role', 'list');
    $('.slick-dots li').removeAttr('role');
    $(".slick-track").attr('aria-label', 'Carousel track');
    $(".slidecarthq-overlay")
      .attr('aria-label', 'Open slide cart')
      .attr('role', 'button')
      .attr('tabindex', '0');
  }

  function fixSlickDotFocus(slider) {
    var $dots = $(slider.$dots).find('button');
    $dots.attr('tabindex', '-1'); // unfocusable
    $(slider.$dots).find('.slick-active button').attr('tabindex', '0'); // active dot focusable
  }

  // Run after Slick initializes and after every slide change
  $(document).on('init afterChange', '.slick-slider', function(event, slick){
    stripAriaSelected();
    fixSlickDotFocus(slick);
  });

  // Safety: run again in case sliders init late via AJAX
  $(document).ready(function(){
    setTimeout(stripAriaSelected, 500);
  });

})(jQuery);



 $(function () {
  var $main = $('.slider-single-nav');
  var $nav  = $('.slider-logo-navigation');
 
  var logoCount = $nav.children().length;

  var showDesktop = Math.min(5, logoCount);
  var show1081    = Math.min(3, logoCount);
  //var show750     = Math.min(3, logoCount);
 
  $main.slick({
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    prevArrow: $('.logo-custom-slider-arrows .logo-slick-cprev'),
    nextArrow: $('.logo-custom-slider-arrows .logo-slick-cnext'),
    dots: true,
    autoplay: true,
    fade: true,
    adaptiveHeight: false,
    touchThreshold: 20,  
    touchMove: true,
    asNavFor: '.slider-logo-navigation',
    accessibility: false,
      
  });

  // logo nav slider
  $nav.slick({
    slidesToShow: showDesktop,
    slidesToScroll: 1,
    asNavFor: '.slider-single-nav',
    arrows: false,
    dots: false,
    autoplay: true,
    swipe: true,
    touchThreshold: 20,  
    touchMove: true,
    accessibility: false,
    focusOnSelect: true,
    centerPadding: 0,
    
    centerMode:   logoCount > showDesktop,
    infinite:     logoCount > showDesktop,
    dots:         logoCount > showDesktop,

    responsive: [
       
      {
        breakpoint: 1081,
        settings: {
          slidesToShow: show1081,
          centerMode: logoCount > show1081,
          infinite:   logoCount > show1081,
          dots:       logoCount > show1081,
          centerPadding: 0
        }
      }, 
      {
        breakpoint: 575,
        settings: {
          slidesToShow: 1,
          centerMode: false,
          infinite:   logoCount > 1,
          dots:       logoCount > 1
        }
      }
    ]
  });
});
 

document.addEventListener("DOMContentLoaded", function () {
  // Create overlay
  const overlay = document.createElement("div");
  overlay.id = "cookie-overlay";
  overlay.style.cssText = `
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.5);
    z-index: 9998;
    display: none;
  `;
  document.body.appendChild(overlay);

  function toggleOverlay() {
    const banner = document.querySelector('.shopify-pc__banner__dialog');
    if (banner) {
      const rect = banner.getBoundingClientRect();
      const visible = rect.width > 0 && rect.height > 0;

      if (visible) {
        overlay.style.display = "block";
        document.body.style.overflow = "hidden";   
      } else {
        overlay.style.display = "none";
        document.body.style.overflow = "";  
      }
    } else {
      overlay.style.display = "none";
      document.body.style.overflow = ""; 
    }
  }

  // Watch DOM changes
  const observer = new MutationObserver(toggleOverlay);
  observer.observe(document.body, { childList: true, subtree: true });

  // Keep checking just in case
  setInterval(toggleOverlay, 500);
});

// Cache each iframe's original src (without autoplay)
$('.tiktok-container-slider iframe').each(function () {
  var $i = $(this);
  $i.attr('data-src-original', $i.attr('src'));
  $i.attr('data-started', '0');
});

function withQuery(url, key, val) {
  var u = new URL(url, window.location.origin);
  u.searchParams.set(key, val);
  return u.toString();
}

function startAutoplay($iframe) {
  // add autoplay=1 & muted=1 only once
  if ($iframe.attr('data-started') === '0') {
    var src = $iframe.attr('src');
    // ensure https absolute for URL parsing
    if (!/^https?:\/\//i.test(src)) {
      var a = document.createElement('a'); a.href = src; src = a.href;
    }
    src = withQuery(src, 'autoplay', '1');
    src = withQuery(src, 'muted', '0'); // improves mobile autoplay reliability
    $iframe.attr('src', src);
    $iframe.attr('data-started', '1');
  }
}

function resetIframe($iframe) {
  // Restore original src (hard stop)
  var original = $iframe.attr('data-src-original');
  if (original) {
    $iframe.attr('src', original);
    $iframe.attr('data-started', '0');
  } else {
    // fallback: reload current src
    var src = $iframe.attr('src');
    $iframe.attr('src', src);
    $iframe.attr('data-started', '0');
  }
}

function deactivateAll() {
  $('.tiktok-container-slider.active iframe').each(function(){
    resetIframe($(this));
  });
  $('.tiktok-container-slider').removeClass('active');
}

// One-tap handler on the container
$('.instagram-slider-row').on('click', '.tiktok-container-slider', function (e) {
  var $container = $(this);
  var $iframe = $container.find('iframe');

  if (!$container.hasClass('active')) {
    // First tap on this slide: activate + autoplay, prevent this tap from going inside
    e.preventDefault();
    deactivateAll();
    $container.addClass('active');
    startAutoplay($iframe);
    // Next taps will go straight into the iframe controls
  }
  // If already active, let the click pass through to the iframe (play/pause/etc.)
});

// When the slide changes, stop any playing TikTok and remove active
$('.instagram-slider-row').on('beforeChange', function (event, slick, current, next) {
  deactivateAll();
});
 
  

  document.addEventListener("DOMContentLoaded", function () {
  const atcButton = document.querySelector(".find-scroll-out");
  const stickyAtc = document.getElementById("sticky-atc");

  if (atcButton && stickyAtc) {
    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(entry => {
          if (!entry.isIntersecting) {
            stickyAtc.style.display = "block";
          } else {
            stickyAtc.style.display = "none";
          }
        });
      },
      { threshold: 0 }
    );

    observer.observe(atcButton);
  }

  // PDP tabs
    const tabLinks = document.querySelectorAll('.tab-link');
      const tabContents = document.querySelectorAll('.product-tab-content');

      tabLinks.forEach(link => {
        link.addEventListener('click', function(e) {
          e.preventDefault();  
          
          // Remove active classes
          tabLinks.forEach(l => l.classList.remove('active'));
          tabContents.forEach(c => c.classList.remove('active'));

          // Activate clicked tab + its content
          this.classList.add('active');
          const targetId = this.getAttribute('data-target');
          const target = document.getElementById(targetId);
          if (target) target.classList.add('active');
        });
      });
  // PDP tabs End

  //PDP accordion start
   const accordionItems = document.querySelectorAll(".acc-item-box");

  accordionItems.forEach((item) => {
    const title = item.querySelector(".acc-sm-title");

    title.addEventListener("click", () => {
      const isActive = item.classList.contains("ac-active");

      // Close all items
      accordionItems.forEach((i) => {
        i.classList.remove("ac-active");
        i.querySelector(".acc-sm-content").style.maxHeight = null;
      });

      // If not active, open the clicked one
      if (!isActive) {
        item.classList.add("ac-active");
        const content = item.querySelector(".acc-sm-content");
        content.style.maxHeight = content.scrollHeight + "px";
      }
    });
  });
  
  const closeBtn = document.querySelector('.mobile-menu-close');   // your button
  const menuToggle = document.querySelector('.header__icon--menu'); // Dawn menu icon

  if (!closeBtn || !menuToggle) return;

  closeBtn.addEventListener('click', () => {
    menuToggle.click();   // simulate the menu icon click
  });

  
 
  function getOuterHeight(el) {
  if (!el) return 0;

  const style = window.getComputedStyle(el);
  return el.offsetHeight +
         parseFloat(style.marginTop) +
         parseFloat(style.marginBottom);
}

function updateStickySpacing() {
  const stickyATC = document.querySelector('.sticky-atc');
  const footer = document.querySelector('.shopify-section-group-footer-group');

  if (!stickyATC || !footer) return;

  const totalHeight = getOuterHeight(stickyATC);
  footer.style.marginBottom = totalHeight + 'px';
}

/* Run after full page load */
window.addEventListener('load', updateStickySpacing);

/* Run when Shopify injects/loads sections */
document.addEventListener('shopify:section:load', updateStickySpacing);
document.addEventListener('shopify:section:unload', updateStickySpacing);
document.addEventListener('shopify:section:select', updateStickySpacing);
document.addEventListener('shopify:section:deselect', updateStickySpacing);

/* Run on resize */
window.addEventListener('resize', updateStickySpacing);

 const sections = document.querySelectorAll('.anim-Inview'); // change this

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const el = entry.target;

        // prevent repeating
        if (!el.classList.contains('in-view')) {
          setTimeout(() => {
            el.classList.add('in-view');
          }, 1000); // 2 seconds delay
        }
      }
    });
  }, { threshold: 0.2 }); // section 20% visible

  sections.forEach(section => observer.observe(section));

});
 
   
$(document).ready(function() {
  
  // Toggle Filter Menu
  $('.filter-btn').on('click', function() {
    $('#filterMenu').toggleClass('show');
    $(this).toggleClass('active');
  });
  
  // Close on outside click
  $(document).on('click', function(e) {
    if (!$(e.target).closest('.question-buttons-wrapper').length) {
      $('#filterMenu').removeClass('show');
      $('.filter-btn').removeClass('active');
    }
  });
  
  // Close menu when any button clicked (mobile only)
  $('.question-btn').on('click', function() {
    if ($(window).width() <= 750) {
      $('#filterMenu').removeClass('show');
      $('.filter-btn').removeClass('active');
    }
  });
  
});
  
document.addEventListener('DOMContentLoaded', () => {
  const fileInput = document.getElementById('ContactForm-file');
  const labelText = document.getElementById('file-label-text');

  if (!fileInput || !labelText) return;

  fileInput.addEventListener('change', () => {
    const files = fileInput.files;

    if (files.length === 0) {
      labelText.textContent = 'Attach Files';
      return;
    }

    if (files.length === 1) {
      labelText.textContent = files[0].name;
    } else {
      labelText.textContent = `${files.length} files selected`;
    }
  });
});

// Nutrition Page animation code
   function checkScroll() {
    const $body = $("body");
    const scrollTop = $(window).scrollTop();

    if (scrollTop > 50) {
        $body.addClass("scrolled");

        setTimeout(function () {
            if (!$(".hnn-sec").is(":visible")) {
                $(".hnn-first").stop(true, true).hide();
                $(".hnn-sec").stop(true, true).fadeIn(600);
            }
        }, 1500);

    } else {
        $body.removeClass("scrolled");

        setTimeout(function () {
            if (!$(".hnn-first").is(":visible")) {
                $(".hnn-sec").stop(true, true).hide();
                $(".hnn-first").stop(true, true).fadeIn(600);
            }
        }, 1500);
    }
}
 
$(document).ready(function () {
    checkScroll();
});
 
$(document).on("scroll", checkScroll);
 
(function () {

  function updateYotpoRating() {
    document.querySelectorAll('.yotpo-sr-bottom-line-summary').forEach(function(button) {

      const scoreEl = button.querySelector('.yotpo-sr-bottom-line-score');
      if (!scoreEl) return;

      let rating = 0.0;

      // Read rating from aria-label
      const aria = button.getAttribute('aria-label');
      if (aria) {
        // Example: "5 out 5 stars rating in total 1 reviews."
        const match = aria.match(/([\d.]+)\s*out\s*5/i);
        if (match && match[1]) {
          rating = parseFloat(match[1]).toFixed(1);
        }
      }

      // If no reviews or invalid
      if (isNaN(rating)) {
        rating = "0.0";
      }

      // Update existing value ONLY
      scoreEl.textContent = rating;
    });
  }

  // Yotpo loads async
  setTimeout(updateYotpoRating, 1200);

  // Run again if Yotpo re-renders
  document.addEventListener('yotpo_initialized', updateYotpoRating);
})();
 
 
 
// document.addEventListener('DOMContentLoaded', () => {
//   const nav = document.querySelector('.toc-inner');
//   if (!nav) return;

//   const links = nav.querySelectorAll('a');
//   const sections = document.querySelectorAll('.toc-content-block');
//   const offset = 125; 
//   let isClickScrolling = false;

//   const setActive = (id) => {
//     links.forEach(link => {
//       link.classList.toggle(
//         'active',
//         link.getAttribute('href') === `#${id}`
//       );
//     });
//   };

//   const scrollToSection = (section) => {
//     isClickScrolling = true;

//     const top =
//       section.getBoundingClientRect().top +
//       window.pageYOffset -
//       offset;

//     window.scrollTo({
//       top,
//       behavior: 'smooth'
//     });

//     setTimeout(() => {
//       isClickScrolling = false;
//     }, 700);
//   };
 
//   links.forEach(link => {
//     link.addEventListener('click', e => {
//       e.preventDefault();

//       const id = link.getAttribute('href').replace('#', '');
//       const target = document.getElementById(id);
//       if (!target) return;

//       history.pushState(null, '', `#${id}`);
//       setActive(id);
//       scrollToSection(target);
//     });
//   });
 
//   window.addEventListener('scroll', () => {
//     if (isClickScrolling) return;

//     let currentId = '';

//     sections.forEach(section => {
//       const top = section.offsetTop - offset - 5;
//       if (window.pageYOffset >= top) {
//         currentId = section.id;
//       }
//     });

//     if (currentId) setActive(currentId);
//   });
 
//   window.addEventListener('load', () => {
//     const hash = window.location.hash;
//     if (!hash) {
//       setActive(sections[0].id);
//       return;
//     }

//     const target = document.querySelector(hash);
//     if (!target) return;

//     setTimeout(() => {
//       scrollToSection(target);
//       setActive(target.id);
//     }, 200);
//   });
// });
   
document.addEventListener('DOMContentLoaded', () => {
  const toc = document.querySelector('.toc-inner');
  if (!toc) return;

  const links = toc.querySelectorAll('a');
  const sections = document.querySelectorAll('.toc-content-block');
  const offset = 125; // sticky header height
  let isClickScrolling = false;

  // TOC activation threshold
  const tocStartPoint = toc.offsetTop - offset;

  const clearActive = () => {
    links.forEach(link => link.classList.remove('active'));
  };

  const setActive = (id) => {
    links.forEach(link => {
      link.classList.toggle(
        'active',
        link.getAttribute('href') === `#${id}`
      );
    });
  };

  const scrollToSection = (section) => {
    isClickScrolling = true;

    const top =
      section.getBoundingClientRect().top +
      window.pageYOffset -
      offset;

    window.scrollTo({
      top,
      behavior: 'smooth'
    });

    setTimeout(() => {
      isClickScrolling = false;
    }, 700);
  };

  // 🔹 CLICK HANDLER
  links.forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();

      const id = link.getAttribute('href').replace('#', '');
      const target = document.getElementById(id);
      if (!target) return;

      history.pushState(null, '', `#${id}`);
      setActive(id);
      scrollToSection(target);
    });
  });

  // 🔹 SCROLL HANDLER
  window.addEventListener('scroll', () => {
    if (isClickScrolling) return;

    // ✅ RESET when scrolling above TOC
    if (window.pageYOffset < tocStartPoint) {
      clearActive();
      return;
    }

    let currentId = '';

    sections.forEach(section => {
      const sectionTop = section.offsetTop - offset - 10;
      if (window.pageYOffset >= sectionTop) {
        currentId = section.id;
      }
    });

    if (currentId) {
      setActive(currentId);
    }
  });

  // 🔹 HASH ON LOAD (ONLY if URL has #id)
  window.addEventListener('load', () => {
    const hash = window.location.hash;
    if (!hash) return;

    const target = document.querySelector(hash);
    if (!target) return;

    setTimeout(() => {
      scrollToSection(target);
      setActive(target.id);
    }, 300);
  });
});
 
  $(document).ready(function() {
    $('.question-btn, .how-it-works-btn').on('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        
        var targetId = $(this).attr('href');
        var targetElement = $(targetId);
        
        if (targetElement.length) {
            var offsetPosition = targetElement.offset().top - 80;
            
            $('html, body').animate({
                scrollTop: offsetPosition
            }, 800);
        }
    });
});


 
 function adjustHeader() {
    if (jQuery('.ann-sticky').length) {
      var annHeight = jQuery('.ann-sticky').outerHeight();
      jQuery('.section-header').css('top', annHeight + 'px');
    }
  }

  jQuery(document).ready(function() {
    if (jQuery('.ann-sticky').length) {
       
      jQuery('.ann-sticky').each(function() {
       jQuery(this).parent().addClass('sticky-ann-active');
      });
 
      setTimeout(adjustHeader, 300);
 
      jQuery(window).resize(function() {
        setTimeout(adjustHeader, 200);
      });
    }
    
  });



// document.addEventListener("click", function (e) {

//   if (!e.target.classList.contains("hover-variant-btn")) return;

//   const btn = e.target;
//   const card = btn.closest(".card-wrapper");
//   if (!card) return;

//   // Remove active only from same group
//   const group = btn.closest(".hover-option-group");
//   group.querySelectorAll(".hover-variant-btn").forEach(b => {
//     b.classList.remove("active");
//   });

//   btn.classList.add("active");

//   // Get selected values by position
//   let option1 = null;
//   let option2 = null;
//   let option3 = null;

//   card.querySelectorAll(".hover-variant-btn.active").forEach(activeBtn => {
//     const position = activeBtn.dataset.optionPosition;
//     const value = activeBtn.dataset.optionValue;

//     if (position == "1") option1 = value;
//     if (position == "2") option2 = value;
//     if (position == "3") option3 = value;
//   });

//   // Get variants JSON
//   const variantsData = JSON.parse(
//     card.querySelector(".variants-json").textContent
//   );

//   // Find matching variant
//   const matchedVariant = variantsData.find(variant => {
//     return (
//       variant.option1 == option1 &&
//       (option2 ? variant.option2 == option2 : true) &&
//       (option3 ? variant.option3 == option3 : true)
//     );
//   });

//   if (matchedVariant) {
//     const hiddenInput = card.querySelector(".product-variant-id");
//     hiddenInput.value = matchedVariant.id;
//     console.log("Selected Variant ID:", matchedVariant.id);
//   } else {
//     console.log("No variant matched");
//   }

// });


// document.addEventListener("click", function (e) {

//   if (e.target.classList.contains("hover-variant-btn")) {

//     const btn = e.target;
//     const variantId = btn.dataset.variantId;

//     const card = btn.closest(".card-wrapper");
//     const hiddenInput = card.querySelector(".product-variant-id");

//     if (hiddenInput) {
//       hiddenInput.value = variantId;
//     }

//     // active state
//     card.querySelectorAll(".hover-variant-btn").forEach(b => {
//       b.classList.remove("active");
//     });

//     btn.classList.add("active");
//   }

// });

document.addEventListener("DOMContentLoaded", function () {

  document.querySelectorAll(".card-wrapper").forEach(card => {
    const firstBtn = card.querySelector(".hover-variant-btn");
    const hiddenInput = card.querySelector(".product-variant-id");

    if (firstBtn) {
      firstBtn.classList.add("active");
      if (hiddenInput) {
        hiddenInput.value = firstBtn.dataset.variantId;
      }
    }
  });

});

document.addEventListener("click", function (e) {

  if (e.target.classList.contains("hover-variant-btn")) {

    const btn = e.target;
    const variantId = btn.dataset.variantId;

    const card = btn.closest(".card-wrapper");
    const hiddenInput = card.querySelector(".product-variant-id");

    if (hiddenInput) {
      hiddenInput.value = variantId;
    }

    card.querySelectorAll(".hover-variant-btn").forEach(b => {
      b.classList.remove("active");
    });

    btn.classList.add("active");
  }

});

//  Accordion — transition and at time one open 

// Wrapper inject
document.querySelectorAll('.accordion__content').forEach(c => {
  if (!c.querySelector('.accordion__content-inner')) {
    c.innerHTML = `<div class="accordion__content-inner rte">${c.innerHTML}</div>`;
  }
});

// Click handler
document.querySelectorAll('.accordion details').forEach(det => {
  const content = det.querySelector('.accordion__content');
  const inner = content.querySelector('.accordion__content-inner');

  if (det.open) content.style.height = inner.offsetHeight + 'px';

  det.querySelector('summary').addEventListener('click', e => {
    e.preventDefault();

    if (det.open) {
      // Close
      content.style.height = inner.offsetHeight + 'px';
      requestAnimationFrame(() => content.style.height = '0');
      content.addEventListener('transitionend', () => {
        det.removeAttribute('open');
        det.classList.remove('is-open');
      }, { once: true });

    } else {
      // Close all others
      document.querySelectorAll('.accordion details[open]').forEach(other => {
        const oc = other.querySelector('.accordion__content');
        oc.style.height = oc.querySelector('.accordion__content-inner').offsetHeight + 'px';
        requestAnimationFrame(() => oc.style.height = '0');
        oc.addEventListener('transitionend', () => {
          other.removeAttribute('open');
          other.classList.remove('is-open');
        }, { once: true });
      });

      // Open this
      det.setAttribute('open', '');
      det.classList.add('is-open');
      const h = inner.offsetHeight;
      content.style.height = '0';
      requestAnimationFrame(() => content.style.height = h + 'px');
      content.addEventListener('transitionend', () => {
        content.style.height = inner.offsetHeight + 'px';
      }, { once: true });
    }
  });
});



document.addEventListener('change', function(e) {

  if (e.target.name === 'id') {

    var variantId = e.target.value;
    var bgWrapper = document.querySelector('.dynamic-product-bg');

    if (!bgWrapper) return;

    var variantImage = window.variantBackgrounds[variantId];

    if (variantImage) {
      bgWrapper.style.backgroundImage = 'url(' + variantImage + ')';
    } else {
      bgWrapper.style.backgroundImage = 'url(' + window.defaultBg + ')';
    }

  }

});


const html = document.documentElement;

const observer = new MutationObserver(function () {
  if (document.body.classList.contains('overflow-hidden')) {
    html.classList.add('cart-open');
  } else {
    html.classList.remove('cart-open');
  }
  if (document.body.classList.contains('overflow-hidden-tablet')) {
    html.classList.add('menu--open');
  } else {
    html.classList.remove('menu--open');
  }
 

});

observer.observe(document.body, {
  attributes: true,
  attributeFilter: ['class']
});

//update price on variant change
jQuery(document).ready(function($) {
$('.hover-variant-btn').on('click', function () {

    var $btn = $(this);
    var $card = $btn.closest('.card-wrapper');

    var price = $btn.data('price');
    var comparePrice = $btn.data('compare-price');

    var $priceEl = $card.find('.price-item--regular');
    var $compareEl = $card.find('.price-item--sale, .price-item--compare');

    // update active button
    $btn.siblings().removeClass('active');
    $btn.addClass('active');

    // update price
    if ($priceEl.length) {
        $priceEl.html(price);
    }

    // update compare price
    if (comparePrice && $compareEl.length) {
        $compareEl.html(comparePrice).show();
    } else {
        $compareEl.hide();
    }
});
});
 



// document.addEventListener("DOMContentLoaded", function() {
//   const productIconLists = document.querySelectorAll('.product__info-wrapper .product__info-container .icon-with-text');
  
//   productIconLists.forEach(list => {
//     const items = list.querySelectorAll('.icon-with-text__item');
//     if (items.length <= 1) return;

//     let currentIndex = 0;
//     items.forEach((item, idx) => {
//       if (idx === 0) item.classList.add('active');
//     });

//     setInterval(() => {
//       items[currentIndex].classList.remove('active');
//       currentIndex = (currentIndex + 1) % items.length;
//       items[currentIndex].classList.add('active');
//     }, 1000); // 1 second interval
//   });
// });



var $items = $('.icon-with-text--vertical .icon-with-text__item');

var current = $items.index($items.filter('.active'));

if (current < 0) {
  current = 0;
  $items.eq(current).addClass('active');
}

setInterval(function () {
  var $currentItem = $items.eq(current);

  // Current → top
  $currentItem.removeClass('active').addClass('leaving');

  current = (current + 1) % $items.length;

  var $nextItem = $items.eq(current);

  // Next → bottom thi center
  $nextItem.addClass('active');

  setTimeout(function () {
    $currentItem.removeClass('leaving');
  }, 500);

}, 3000);

  
document.addEventListener("click", function(e) {
  const bar = e.target.closest(".klaviyo_form_trigger");

  if (bar) {
    e.preventDefault();

    function openKlaviyo(attempts = 10) {
      if (window._klOnsite && typeof window._klOnsite.openForm === "function") {
        window._klOnsite.openForm('WW78Wf');
  
      } else if (attempts > 0) {
        setTimeout(() => openKlaviyo(attempts - 1), 300);
      }
    }

    openKlaviyo();
  }
});
  



$(document).ready(function () {
  const $slider = $(".js-ingredients-slick");
  
  if ($slider.length && typeof $.fn.slick !== "undefined") {
    $slider.slick({
      slidesToShow: 5,
      slidesToScroll: 1,
      infinite: false,
      arrows: true,
      dots: false, 
      touchThreshold: 25, 
      touchMove: true,      
      prevArrow: $(".slick-custom-prev"),
      nextArrow: $(".slick-custom-next"),
      responsive: [
        {
          breakpoint: 1601,
          settings: {
            slidesToShow: 4
          }
        },
        {
          breakpoint: 1281,
          settings: {
            slidesToShow: 3
          }
        },
        {
          breakpoint: 1025,
          settings: {
            slidesToShow: 2
          }
        },
        {
          breakpoint: 750,
          settings: {
            slidesToShow: 1.2, 
            slidesToScroll: 1,
            arrows: false,     
            dots: true         
          }
        }
      ]
    });
  }

  const $wrapper = $(".js-cursor-wrapper");
  const $cursor = $(".custom-cursor");
  const $btns = $(".slider-btn");

  if ($wrapper.length && $cursor.length) {
      // Cursor class while slider dragging
    $slider.on("mousedown touchstart", function () {
      $cursor.addClass("is-dragging");
    });

    $(document).on("mouseup touchend touchcancel", function () {
      $cursor.removeClass("is-dragging");
    });
    
    $wrapper.on("mouseenter", function () {
      if (window.innerWidth > 750) {
        $cursor.addClass("is-active custom-cursor__visible");
      }
    });

    $wrapper.on("mouseleave", function () {
      $cursor.removeClass("is-active custom-cursor__visible");
    });

    $wrapper.on("mousemove", function (e) {
      if (window.innerWidth > 750) {
        $cursor.css({
          left: e.clientX + "px",
          top: e.clientY + "px"
        });
      }
    });

    $btns.on("mouseenter", function () {
      $cursor.removeClass("is-active custom-cursor__visible");
    });
    
    $btns.on("mouseleave", function () {
      if ($wrapper.is(":hover") && window.innerWidth > 750) {
        $cursor.addClass("is-active custom-cursor__visible");
      }
    });
  }
});